"use client";

import React, { useMemo } from "react";
import { getColorFromDivergingScale, getColorFromSequentialScale } from "@/utils/colors";
import type { CellComponentProps } from "../DataTable.types";

export interface PercentBarCellProps extends CellComponentProps {
  value: number;
  showLabel?: boolean;
  color?: string | "auto";
  backgroundColor?: string | "none";
  height?: number;
  segments?: number;
  segmentGap?: number;
  colorScale?: {
    type: "sequential" | "diverging";
    scheme?: string;
    center?: number;
  };
}

/**
 * PercentBarCell
 *
 * Horizontal bar showing percentage (0-100%).
 * Perfect for battery charge, progress indicators, and utilization metrics.
 * Supports segmented "LED meter" style with optional gradient coloring.
 */
export const PercentBarCell = React.memo<PercentBarCellProps>(
  ({
    value,
    showLabel = true,
    color = "var(--color-viz-categorical-1)",
    backgroundColor = "var(--color-background-muted)",
    height = 20,
    segments,
    segmentGap = 2,
    colorScale,
    context,
  }) => {
    // Clamp value to 0-100
    const percentage = useMemo(() => {
      return Math.max(0, Math.min(100, value));
    }, [value]);

    // Get the effective color
    const effectiveColor = useMemo(() => {
      if (color === "auto" && colorScale) {
        if (colorScale.type === "diverging" && colorScale.center !== undefined) {
          return getColorFromDivergingScale(value, 0, 100, colorScale.center, colorScale.scheme);
        }
        return getColorFromSequentialScale(value, 0, 100, colorScale.scheme);
      }
      return color;
    }, [color, colorScale, value]);

    // Loading state
    if (context.isLoading) {
      return (
        <div className="flex items-center gap-2" style={{ height }}>
          <div className="h-full w-full animate-pulse bg-border-default rounded" />
        </div>
      );
    }

    // Segmented rendering
    if (segments && segments > 0) {
      const totalGapWidth = segmentGap * (segments - 1);
      const segmentWidth = `calc((100% - ${totalGapWidth}px) / ${segments})`;
      const filledSegments = Math.ceil((percentage / 100) * segments);

      return (
        <div className="flex items-center gap-2 w-full">
          <div className="flex gap-0.5 flex-1" style={{ height }}>
            {Array.from({ length: segments }).map((_, i) => {
              const isFilled = i < filledSegments;

              // Get color for this segment if using auto color
              let segmentColor = effectiveColor;
              if (color === "auto" && colorScale && isFilled) {
                const segmentPercent = ((i + 1) / segments) * 100;
                if (colorScale.type === "diverging" && colorScale.center !== undefined) {
                  segmentColor = getColorFromDivergingScale(
                    segmentPercent,
                    0,
                    100,
                    colorScale.center,
                    colorScale.scheme
                  );
                } else {
                  segmentColor = getColorFromSequentialScale(segmentPercent, 0, 100, colorScale.scheme);
                }
              }

              return (
                <div
                  key={i}
                  className="rounded-sm"
                  style={{
                    width: segmentWidth,
                    backgroundColor: isFilled
                      ? segmentColor
                      : backgroundColor === "none"
                        ? "transparent"
                        : backgroundColor,
                    marginRight: i < segments - 1 ? `${segmentGap}px` : 0,
                  }}
                />
              );
            })}
          </div>
          {showLabel && <span className="text-sm text-text-body whitespace-nowrap">{percentage.toFixed(0)}%</span>}
        </div>
      );
    }

    // Standard bar rendering
    return (
      <div className="flex items-center gap-2 w-full">
        <div
          className="relative flex-1 rounded-sm"
          style={{
            height,
            backgroundColor: backgroundColor === "none" ? "transparent" : backgroundColor,
          }}
        >
          <div
            className="absolute left-0 top-0 h-full rounded-sm transition-all duration-300"
            style={{
              width: `${percentage}%`,
              backgroundColor: effectiveColor,
            }}
          />
        </div>
        {showLabel && <span className="text-sm text-text-body whitespace-nowrap">{percentage.toFixed(0)}%</span>}
      </div>
    );
  }
);

PercentBarCell.displayName = "PercentBarCell";
